import type React from "react";

interface SparkleStarsProps {
  seed: string;
  color: string;
  glowColor: string;
  className?: string;
}

const StarIcon: React.FC<{ className?: string; style?: React.CSSProperties }> = ({
  className,
  style,
}) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0L15 9L24 12L15 15L12 24L9 15L0 12L9 9Z" />
  </svg>
);

const getSeedFromString = (str: string | undefined | null) => {
  const safeStr = typeof str === "string" ? str : String(str || "");
  let hash = 0;
  for (let i = 0; i < safeStr.length; i++) {
    hash = (hash << 5) - hash + safeStr.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const SparkleStars: React.FC<SparkleStarsProps> = ({
  seed,
  color,
  glowColor,
  className = "",
}) => {
  const hashSeed = getSeedFromString(seed);

  // Simple LCG-style pseudo-random number generator
  const getPseudoRandom = (index: number) => {
    return ((hashSeed * 9301 + 49297) * (index + 1)) % 233280;
  };

  const getStarProps = (index: number, baseSize: number, defaultDx: number) => {
    const pr1 = getPseudoRandom(index * 3);
    const pr2 = getPseudoRandom(index * 3 + 1);
    const pr3 = getPseudoRandom(index * 3 + 2);

    // Randomize size: +/- 40%
    const sizeMultiplier = 0.6 + (0.8 * (pr1 % 100)) / 100;
    const finalSize = Math.round(baseSize * sizeMultiplier * 10) / 10;

    // Randomize offsets: +/- 8px for X and +/- 6px for Y
    const dx = defaultDx + ((pr2 % 17) - 8);
    const dy = (pr3 % 13) - 6;

    return {
      size: `${finalSize}px`,
      transform: `translate(${dx}px, ${dy}px)`,
    };
  };

  const baseSizes = [14, 10, 8];
  const permutationIndex = hashSeed % 6;
  const permutations = [
    [0, 1, 2],
    [0, 2, 1],
    [1, 0, 2],
    [1, 2, 0],
    [2, 0, 1],
    [2, 1, 0],
  ];
  const order = permutations[permutationIndex];

  const star1 = getStarProps(0, baseSizes[order[0]], 0); // Slot 1 gets shuffled base size
  const star2 = getStarProps(1, baseSizes[order[1]], -4); // Slot 2 gets shuffled base size
  const star3 = getStarProps(2, baseSizes[order[2]], -2); // Slot 3 gets shuffled base size

  return (
    <div
      className={`flex flex-col items-center justify-between h-[46px] opacity-75 select-none pointer-events-none z-10 ${className}`}
    >
      <StarIcon
        style={{
          width: star1.size,
          height: star1.size,
          transform: star1.transform,
          color,
          filter: `drop-shadow(0 0 4px ${glowColor}) brightness(1.2)`,
          transition: "transform 0.2s ease-out",
        }}
      />
      <StarIcon
        style={{
          width: star2.size,
          height: star2.size,
          transform: star2.transform,
          color,
          filter: `drop-shadow(0 0 4px ${glowColor}) brightness(1.2)`,
          transition: "transform 0.2s ease-out",
        }}
      />
      <StarIcon
        style={{
          width: star3.size,
          height: star3.size,
          transform: star3.transform,
          color,
          filter: `drop-shadow(0 0 4px ${glowColor}) brightness(1.2)`,
          transition: "transform 0.2s ease-out",
        }}
      />
    </div>
  );
};
